import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Button, Chip, Textarea } from '@coordio/ui';
import {
  Play as PlayIcon,
  ChevronDown as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  CircleAlert as ErrorIcon,
} from 'lucide-react';
import { externalSystemService } from '../../services/externalSystemService';
import { useNotificationStore } from '../../store/notificationStore';
import type { ApiExecutionResult } from '../../types/externalSystem';

interface ApiMethodTesterProps {
  systemId: string;
  methodId: string;
}

/**
 * API method tester component for testing API method execution.
 */
export const ApiMethodTester = ({ systemId, methodId }: ApiMethodTesterProps) => {
  const { addNotification } = useNotificationStore();

  const [parametersJson, setParametersJson] = useState('{}');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<ApiExecutionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    setError(null);
    setResult(null);

    let parameters: Record<string, any> = {};
    if (parametersJson.trim()) {
      try {
        parameters = JSON.parse(parametersJson.trim());
      } catch {
        setError('Parameters JSON must be valid JSON');
        return;
      }
    }

    setTesting(true);
    try {
      const testResult = await externalSystemService.testApiMethod(systemId, methodId, { parameters });
      setResult(testResult);
      if (testResult.isSuccess) {
        addNotification('API test executed successfully', 'success');
      } else {
        addNotification(`API test failed: ${testResult.errorMessage}`, 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to test API method';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <h6 className="text-lg font-semibold mb-3">Test API Method</h6>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Parameters (JSON)</label>
            <Textarea className="w-full" rows={6} value={parametersJson} onChange={(e) => setParametersJson(e.target.value)} />
            <p className="text-xs text-[var(--color-text-secondary)]">JSON object with parameters for the API call</p>
          </div>
        </div>

        <div className="col-span-12">
          <Button onClick={handleTest} disabled={testing}>
            {testing ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Testing...
              </>
            ) : (
              <>
                <PlayIcon className="mr-2 h-4 w-4" />
                Test API Method
              </>
            )}
          </Button>
        </div>

        {error && (
          <div className="col-span-12">
            <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          </div>
        )}

        {result && (
          <div className="col-span-12">
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className="flex items-center gap-2">
                  {result.isSuccess ? (
                    <SuccessIcon style={{ color: 'var(--mui-palette-success-main)' }} />
                  ) : (
                    <ErrorIcon style={{ color: 'var(--mui-palette-error-main)' }} />
                  )}
                  <span className="text-base font-medium">
                    Test Result - HTTP {result.statusCode} ({result.durationMs}ms)
                  </span>
                  <Chip
                    label={result.isSuccess ? 'Success' : 'Failed'}
                    color={result.isSuccess ? 'green' : 'red'}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  {result.errorMessage && (
                    <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400 mb-2">
                      {result.errorMessage}
                    </div>
                  )}

                  {result.responseBody && (
                    <div className="mb-2">
                      <h6 className="text-sm font-medium mb-1">Response Body:</h6>
                      <Textarea
                        className="w-full font-mono text-sm"
                        rows={10}
                        value={result.responseBody}
                        readOnly
                      />
                    </div>
                  )}

                  {Object.keys(result.responseHeaders).length > 0 && (
                    <div>
                      <h6 className="text-sm font-medium mb-1">Response Headers:</h6>
                      <pre className="bg-muted p-1 rounded text-xs overflow-auto">
                        {JSON.stringify(result.responseHeaders, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
};
